/**
 * Camera capture utilities — Yuri spec Iter. 337
 *
 * Stabilize the meal-photo capture on Samsung S20/S21 FE where the browser
 * WebView often refuses to open the camera on a `display: none` file input
 * or silently fails on getUserMedia.
 *
 * Strategy:
 *  1. PROBE the device (permissions API + private-mode heuristic) BEFORE
 *     trying to open the camera, so we can warn the user up-front.
 *  2. TRIGGER the file input from an actual user gesture (no setTimeout!).
 *     Some Samsung browsers drop the click if it's deferred — that's the
 *     #1 cause of "appareil photo ne s'ouvre pas".
 *  3. Use `accept="image/*"` + `capture="environment"` to force the OS
 *     native camera app, bypassing all WebView capture bugs.
 *  4. If the user dismisses the chooser without taking a photo, surface a
 *     friendly toast with a "Galerie" fallback CTA.
 */

import { toast } from 'sonner';

/**
 * Detect Samsung Internet / Samsung WebView (the main offender).
 */
export function isSamsungBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = (navigator.userAgent || '').toLowerCase();
  return ua.includes('samsungbrowser') || ua.includes('samsung');
}

/**
 * Detect iOS (iPhone/iPad). Used to skip Android-specific heuristics that
 * generate false positives on Safari iOS (notably the storage quota check).
 */
export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports as Mac with touch support
    (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1);
}

/**
 * Best-effort private/incognito detection.
 *
 * ⚠️ iOS Safari ALWAYS returns a small storage quota (sometimes <50 MB) even
 *    in normal mode → the old `quota < 120 MB` heuristic produced 100% false
 *    positives for every iPhone user (Marc, Erik, Patrick…). We now SKIP the
 *    storage heuristic entirely on iOS and rely only on the indexedDB probe.
 *
 * Returns a Promise<boolean>.
 */
export async function isPrivateBrowsing() {
  const ios = isIOS();
  try {
    // ANDROID-ONLY: storage quota < 120 MB strongly suggests private mode.
    // We skip this entirely on iOS because Safari quotas are always tiny.
    if (!ios && typeof navigator !== 'undefined' && navigator.storage && navigator.storage.estimate) {
      const { quota } = await navigator.storage.estimate();
      if (typeof quota === 'number' && quota < 120 * 1024 * 1024) return true;
    }
  } catch { /* noop */ }

  // Safari Private — opening IndexedDB throws or returns null
  try {
    const dbReq = window.indexedDB && window.indexedDB.open('yumix_private_probe');
    if (!dbReq) return false;
    return await new Promise((resolve) => {
      dbReq.onerror = () => resolve(true);
      dbReq.onsuccess = () => { try { dbReq.result?.close(); } catch { /* noop */ } resolve(false); };
      // safety timeout — assume NOT private if probe is just slow
      setTimeout(() => resolve(false), 800);
    });
  } catch {
    return true;
  }
}

/**
 * Probe getUserMedia permissions without actually opening the camera.
 * Returns 'granted' | 'denied' | 'prompt' | 'unknown'.
 */
export async function probeCameraPermission() {
  try {
    if (typeof navigator === 'undefined' || !navigator.permissions || !navigator.permissions.query) {
      return 'unknown';
    }
    const status = await navigator.permissions.query({ name: 'camera' });
    return status.state || 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Trigger the hidden file input as native camera capture.
 *
 * IMPORTANT: must be called synchronously from a user-gesture event
 * handler (onClick) on Samsung browsers. NO setTimeout, NO async work
 * before the .click() — or Samsung Internet swallows the event.
 *
 * @param {React.RefObject<HTMLInputElement>} inputRef
 * @param {object}  opts
 * @param {string}  opts.language        'fr' | 'en'
 * @param {Function} [opts.onUnsupported] Fired when the device can't capture at all.
 */
export function openNativeCamera(inputRef, { language = 'fr', onUnsupported } = {}) {
  const fr = String(language).startsWith('fr');
  const input = inputRef?.current;
  if (!input) {
    toast.error(fr ? 'Capture indisponible sur ce navigateur.' : 'Capture unavailable on this browser.');
    if (typeof onUnsupported === 'function') onUnsupported();
    return false;
  }
  // Force the right attributes every time (some Android WebViews strip them after first use)
  try {
    input.setAttribute('accept', 'image/*');
    input.setAttribute('capture', 'environment');
  } catch { /* noop */ }

  // Synchronous click — keeps the user-gesture chain intact for Samsung Internet
  try {
    input.click();
    return true;
  } catch (e) {
    // Last-resort: dispatch a synthetic MouseEvent
    try {
      input.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      return true;
    } catch {
      toast.error(fr ? 'Impossible d\'ouvrir l\'appareil photo.' : 'Cannot open the camera.');
      if (typeof onUnsupported === 'function') onUnsupported();
      return false;
    }
  }
}

/**
 * Pre-flight check before opening the camera.
 *
 * Strategy (Mai 2026 — Marc/Erik/Patrick/Juan iPhone+Android bug fix):
 *   Native `<input type="file" capture>` works on iOS Safari and Samsung
 *   Internet EVEN when our heuristics suspect private mode. Blocking the
 *   capture caused 100% of iPhone users to land back on the dashboard with
 *   no feedback. New behaviour: only the EXPLICIT denied state hard-blocks
 *   the call. Anything else proceeds with at most a non-blocking warning.
 *
 * Resolves to `true` if the caller should proceed with `openNativeCamera`.
 */
export async function checkCameraReadiness({ language = 'fr' } = {}) {
  const fr = String(language).startsWith('fr');

  // 1) Permission EXPLICITLY denied → block, guide user to settings.
  //    `prompt` and `unknown` proceed (user will see the OS dialog).
  const perm = await probeCameraPermission();
  if (perm === 'denied') {
    toast.error(
      fr
        ? 'Accès à l\'appareil photo refusé. Va dans Paramètres → Applications → Chrome/Safari → Permissions et active la Caméra.'
        : 'Camera access denied. Go to Settings → Apps → Chrome/Safari → Permissions and enable Camera.',
      { duration: 9000, id: 'camera-denied' }
    );
    return false;
  }

  // 2) Private mode → WARN but proceed. The native picker still opens.
  //    Only Android (Chrome) really blocks hardware; iOS Safari does not.
  //    We don't await this — fire-and-forget so the user-gesture chain
  //    isn't broken between this check and `input.click()` (Samsung issue).
  isPrivateBrowsing().then((priv) => {
    if (!priv) return;
    toast.warning(
      fr
        ? 'Si la caméra ne s\'ouvre pas, vérifie que tu n\'es pas en navigation privée.'
        : 'If the camera does not open, check you are not in private browsing.',
      { duration: 6000, id: 'camera-private-warn' }
    );
  }).catch(() => {});

  return true;
}
